/**
 * Which columns a table shows — and therefore which the download carries.
 *
 * ONE CHOICE, BOTH OUTPUTS. A picker that hid columns on screen but exported
 * everything would make the file quietly different from the table it came
 * from, which is the whole thing the download was meant not to be.
 *
 * Pure, so the rules are testable without a DOM.
 */

export interface ColumnDef {
  key: string;
  label: string;
  /** A column the table cannot be read without — the name it is keyed by. */
  always?: boolean;
}

const KEY = 'jcseo.boardroom.columns';

/** A choice per ORIENTATION. Flipped, the picker lists the products; upright
 *  it lists the fields — the same key means "hide this company" one way round
 *  and "hide this price" the other, so the two cannot share a bucket. */
function bucket(table: string, flipped = false): string {
  return `${KEY}.${table}${flipped ? '.flipped' : ''}`;
}

/** Hidden rather than shown, so a column added later appears by default
 *  instead of being invisible to everyone who ever used the picker.
 *  `defaults` are hidden until the picker is first used on this table. */
export function storedHidden(table: string, flipped = false, defaults: string[] = []): Set<string> {
  try {
    const raw = window.localStorage.getItem(bucket(table, flipped));
    return new Set<string>(raw ? (JSON.parse(raw) as string[]) : defaults);
  } catch {
    return new Set(defaults);
  }
}

export function rememberHidden(table: string, hidden: Set<string>, flipped = false): void {
  try {
    window.localStorage.setItem(bucket(table, flipped), JSON.stringify(Array.from(hidden)));
  } catch {
    /* private mode: the default is fine */
  }
}

export function toggle(hidden: Set<string>, key: string): Set<string> {
  const next = new Set(hidden);
  if (next.has(key)) next.delete(key);
  else next.add(key);
  return next;
}

/** The columns to render, in declared order. An `always` column can never be
 *  hidden, however stale the stored choice is. */
export function visible<T extends ColumnDef>(defs: T[], hidden: Set<string>): T[] {
  return defs.filter(d => d.always || !hidden.has(d.key));
}

/** "9 of 13" — or nothing when they are all showing, because a control that
 *  reads the same whether or not it is doing anything is noise. */
export function pickedLabel(defs: ColumnDef[], hidden: Set<string>): string {
  const shown = visible(defs, hidden).length;
  return shown === defs.length ? '' : `${shown} of ${defs.length}`;
}
